import React, { useEffect, useState } from 'react';
import TabComponent from '../../generic-components/customTabNavigation/TabNavigation';
import {
    applicationListTableColumn,
    applicationTableVisibleTabs,
    scopeKey
} from '../../config';
import CustomTable from '../../generic-components/customTable/CustomTable';
import NoDataFound from '../../generic-components/NoDataFound/NoDataFound';
import CutButton from '../../generic-components/customCutButton/CutButton';
import ClayLayout from '@clayui/layout';
import ClayCard from '@clayui/card';
import ProfileCard from '../../generic-components/profile/ProfileCard';
import { useNavigate } from 'react-router-dom';
import { useApiMutation } from '../../apis/useApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import DebouncedSearch from '../../generic-components/DebouncedSearch/DebouncedSearch';
import { useModal } from '@clayui/core';
import FiltersModal from '../../generic-components/FilterModal/FiltersModal';
import {
    buildLiferayFilterQuery,
    FilterObject,
    matchMakingFilterFields
} from '../../utils/matchMaking.config';
import { convertFromISO } from '../../utils/DateFormat';

export interface ApiResponse {
    totalCount: number;
    items: [];
}

const INIT_STATUS = {
    requestDate: [],
    requestorStatus: []
};

const ApplicationList = () => {
    const [active, setActive] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] =
        useState(searchValue);
    const { observer, onOpenChange, open } = useModal();

    const [isLoading, setIsLoading] = useState(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [dropdownOptions, setDropdownOptions] = useState<any>({});
    const [startIndex, setStartIndex] = useState(0);
    const [filters, setFilters] = useState<FilterObject>(INIT_STATUS);
    const userEmail = useSelector(
        (state: RootState) => state.userDetailsSlice.userDetails.emailAddress
    );

    const [matchMakingTableData, setMatchMakingTableData] = useState<any[]>([]);
    const navigate = useNavigate();

    const getmatchMakingData = useApiMutation<ApiResponse, {}>(
        `/o/c/matchmakingrequestors/scopes/${encodeURIComponent(scopeKey)}`,
        'get',
        {
            onSuccess: (data) => {
                if (data) {
                    const formatted = data?.items?.map((item: any) => ({
                        id: item?.id,
                        requestorName: `${item?.requestorFirstName} ${item?.requestorLastName}`,
                        requestedService:
                            item?.requestorWantedCategoriesToBeConnected,
                        requestDate: item?.dateCreated,
                        otherRequestedService:
                            item?.otherCategoriesWantedToConnectInDubai,
                        matchMakingStatus: item?.requestorStatus?.name
                    }));
                    setMatchMakingTableData(formatted);
                    setTotalItems(data?.totalCount);
                    setIsLoading(false);
                }
            },
            onError: (error: any) => {
                setIsLoading(false);
                console.error('Mutation error:', error);
            }
        }
    );

    const getMatchMakingFilterData = useApiMutation<ApiResponse, {}>(
        `/o/c/matchmakingrequestors/scopes/${encodeURIComponent(scopeKey)}`,
        'get',
        {
            onSuccess: (data) => {
                if (data) {
                    const dropdownOptions: {
                        [key: string]: { key: string; name: string }[];
                    } = {};

                    matchMakingFilterFields.forEach((field) => {
                        const key = field.name;
                        data.items.forEach((item: any) => {
                            let value: string = '';
                            let displayName: string = '';

                            switch (key) {
                                case 'requestDate':
                                    value = convertFromISO(item.dateCreated);
                                    displayName = convertFromISO(
                                        item.dateCreated
                                    );
                                    break;
                                case 'requestorStatus':
                                    value = item.requestorStatus?.key;
                                    displayName = item.requestorStatus?.name;
                                    break;
                                default:
                                    return;
                            }
                            if (!value) return;

                            if (!dropdownOptions[key]) {
                                dropdownOptions[key] = [];
                            }

                            // Ensure unique entries based on 'value'
                            if (
                                !dropdownOptions[key].some(
                                    (entry) => entry.key === value
                                )
                            ) {
                                dropdownOptions[key].push({
                                    key: value,
                                    name: displayName
                                });
                            }
                        });
                    });
                    setDropdownOptions((prevData: any) => ({
                        ...prevData,
                        ...dropdownOptions
                    }));
                    setIsLoading(false);
                }
            },
            onError: (error: any) => {
                setIsLoading(false);
                console.error('Mutation error:', error);
            }
        }
    );

    const fetchMatchMakingList = (
        page: number,
        size: number,
        search: string,
        appliedFilters: FilterObject,
        activeTab: number
    ) => {
        setIsLoading(true);

        const liferayFilter = buildLiferayFilterQuery(
            appliedFilters,
            activeTab,
            userEmail
        );

        const params = {
            page,
            pageSize: size,
            sort: 'dateCreated:desc',
            restrictFields: 'actions',
            search,
            ...(liferayFilter && { filter: liferayFilter }) // 🔹 APPLY FILTER HERE
        };

        getmatchMakingData.mutate({
            data: {},
            params
        });
    };

    useEffect(() => {
        fetchMatchMakingList(
            currentPage,
            pageSize,
            debouncedSearchQuery,
            filters,
            active
        );
    }, [userEmail, pageSize, currentPage, debouncedSearchQuery]);

    useEffect(() => {
        const params: {
            page: number;
            pageSize: number;
            sort?: any;
            restrictFields?: string;
            filter?: string;
        } = {
            page: 1,
            pageSize: 0,
            sort: 'dateCreated:desc',
            restrictFields: 'actions',
            filter: `requestorWorkEmail eq '${userEmail}'`
        };

        getMatchMakingFilterData.mutate({
            data: {},
            params: params
        });
    }, [userEmail, active]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchValue);
        }, 1000); // 1000ms debounce

        return () => {
            clearTimeout(handler); // Clear the timeout if the searchQuery changes before 500ms
        };
    }, [searchValue]);

    const handlePageChange = (page: number) => {
        setStartIndex(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setStartIndex(0);
    };

    const handleView = (detail: any) => {
        if (
            [
                'Need Revision by Account Manager',
                'Pending Details from Requestor'
            ].includes(detail?.matchMakingStatus)
        ) {
            navigate(`/edit-request/${detail?.id}`);
        } else {
            navigate(`/application-detail/${detail?.id}`);
        }
    };

    const handleFilterApply = (additionalFilters?: FilterObject) => {
        // 🔹 CLOSE MODAL
        open && onOpenChange(false);

        // 🔹 RESET PAGINATION WHEN APPLYING FILTER
        setCurrentPage(1);
        setStartIndex(0);

        // 🔹 TRIGGER API WITH NEW FILTERS
        fetchMatchMakingList(
            1,
            pageSize,
            debouncedSearchQuery,
            {
                ...filters,
                ...additionalFilters
            },
            active
        );
    };

    const handleSetActive = (active: number) => {
        setActive(active);
        fetchMatchMakingList(
            1,
            pageSize,
            debouncedSearchQuery,
            INIT_STATUS,
            active
        );
    };

    return (
        <>
            <ClayLayout.ContentSection className="button-group-container mt-0 mb-4">
                <CutButton
                    title="Create New Request"
                    buttonType="button"
                    onClick={() => navigate(`/create-request`)}
                    disabled={isLoading}
                />
            </ClayLayout.ContentSection>
            <div className="customer-profile-wrapper">
                <ClayCard className="sidebar-card topBottomCut p-4">
                    <ClayCard.Body className="p-0">
                        <ProfileCard />
                    </ClayCard.Body>
                </ClayCard>
                <div
                    className="pl-4  mt-4 customer-table-tabs-container"
                    style={{ backgroundColor: '#f6f9fa' }}
                >
                    <TabComponent
                        active={active}
                        setActive={handleSetActive}
                        visibleTabs={applicationTableVisibleTabs}
                    />
                </div>
                <div
                    className="p-4 mt-4"
                    style={{ backgroundColor: '#f6f9fa' }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="table-title">
                            {applicationTableVisibleTabs[active]?.title}{' '}
                            Applications
                        </h5>
                        <div className="d-flex align-items-center">
                            <DebouncedSearch
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                placeholder="Search"
                            />
                            <div
                                onClick={() => onOpenChange(true)}
                                className="ml-2"
                            >
                                <svg
                                    width="40"
                                    height="40"
                                    viewBox="0 0 30 30"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="29"
                                        height="29"
                                        rx="3.5"
                                        stroke="#003764"
                                    />
                                    <path
                                        d="M14.8061 20.8327C14.6227 20.8327 14.47 20.7716 14.3477 20.6493C14.2255 20.5271 14.1644 20.3743 14.1644 20.191V15.5443L9.66774 9.85768C9.55552 9.70768 9.54024 9.5549 9.62191 9.39935C9.70358 9.24379 9.83413 9.16602 10.0136 9.16602H19.9819C20.1614 9.16602 20.2919 9.24379 20.3736 9.39935C20.4552 9.55602 20.44 9.70879 20.3277 9.85768L15.8311 15.5452V20.1927C15.8311 20.3755 15.77 20.528 15.6477 20.6502C15.5255 20.7724 15.3727 20.8335 15.1894 20.8335L14.8061 20.8327ZM14.9977 15.2493L19.1227 9.99935H10.8727L14.9977 15.2493Z"
                                        fill="#003764"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {matchMakingTableData &&
                    matchMakingTableData?.length > 0 ? (
                        <CustomTable
                            data={matchMakingTableData || []} // Pass the table data
                            columns={applicationListTableColumn || []} // Pass the columns
                            handleView={handleView} // Pass the handleView function
                            startIndex={startIndex} // Pass the start index for pagination
                            pageSize={pageSize} // Pass the page size
                            onPageChange={handlePageChange} // Pass the function to handle page change
                            onPageSizeChange={handlePageSizeChange} // Pass the function to handle page size change
                            isLoading={isLoading} // Pass loading state
                            totalItems={totalItems}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            isShowPopUp={true}
                        />
                    ) : (
                        <NoDataFound />
                    )}
                    <FiltersModal
                        open={open}
                        observer={observer}
                        setFilters={setFilters}
                        getFilterBasedEventData={handleFilterApply}
                        filterFields={matchMakingFilterFields}
                        dropdownOptions={dropdownOptions}
                    />
                </div>
            </div>
        </>
    );
};

export default ApplicationList;
